import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  Save,
  RefreshCw,
  Loader2,
  Plus,
  Eye,
  EyeOff,
  Sparkles,
  Check,
  Cloud,
} from 'lucide-react'
import { SortableSection } from './SortableSection'
import { SECTION_EDITORS } from './SectionEditors'
import { RichTextEditor } from './RichTextEditor'
import { ImageUploader } from './ImageUploader'
import type { SiteContent } from '@/lib/api'
import { cn } from '@/lib/utils'

const SECTION_TYPES = [
  { type: 'features', label: 'Features Grid' },
  { type: 'benefits', label: 'Benefits List' },
  { type: 'testimonials', label: 'Testimonials' },
  { type: 'pricing', label: 'Pricing Card' },
  { type: 'faq', label: 'FAQ Section' },
  { type: 'cta', label: 'Call to Action' },
  { type: 'content', label: 'Text Content' },
  { type: 'articles', label: 'Article Grid' },
  { type: 'categories', label: 'Categories' },
  { type: 'newsletter', label: 'Newsletter Signup' },
]

function getDefaultSectionContent(type: string): unknown {
  switch (type) {
    case 'features':
      return { features: [{ title: 'Feature 1', description: 'Description' }] }
    case 'benefits':
      return { benefits: [{ title: 'Benefit 1', description: 'Description' }] }
    case 'testimonials':
      return { testimonials: [{ quote: 'Great!', author: 'Customer' }] }
    case 'pricing':
      return { price: '$29/mo', features: ['Feature 1'], ctaText: 'Get Started', ctaUrl: '#' }
    case 'faq':
      return { items: [{ question: 'Question?', answer: 'Answer.' }] }
    case 'cta':
      return { description: 'Ready to get started?' }
    case 'content':
      return { text: '<p>Your content here...</p>' }
    case 'articles':
      return { articles: [{ title: 'Article', excerpt: '...', category: 'News', date: new Date().toISOString().split('T')[0], readTime: '3 min' }] }
    case 'categories':
      return { categories: [{ name: 'Category', count: 10, icon: '📁' }] }
    case 'newsletter':
      return { description: 'Subscribe', placeholder: 'Email', buttonText: 'Subscribe' }
    default:
      return {}
  }
}

interface ContentEditorProps {
  content: SiteContent
  onChange: (content: SiteContent) => void
  onSave: () => void
  onRegenerate: (type: 'content' | 'section', sectionIndex?: number) => void
  isSaving: boolean
  isRegenerating: boolean
  previewHtml?: string
  previewLoading?: boolean
  siteId?: string
}

export function ContentEditor({
  content,
  onChange,
  onSave,
  onRegenerate,
  isSaving,
  isRegenerating,
  previewHtml,
  previewLoading,
  siteId,
}: ContentEditorProps) {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set())
  const [showPreview, setShowPreview] = useState(true)
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved')
  const [lastSavedContent, setLastSavedContent] = useState<string>(JSON.stringify(content))
  const [regeneratingSection, setRegeneratingSection] = useState<number | null>(null)

  // Auto-save debounce
  useEffect(() => {
    const currentContent = JSON.stringify(content)
    if (currentContent !== lastSavedContent) {
      setAutoSaveStatus('unsaved')
      const timer = setTimeout(() => {
        setAutoSaveStatus('saving')
        onSave()
        setLastSavedContent(currentContent)
        setTimeout(() => setAutoSaveStatus('saved'), 500)
      }, 2000) // 2 second debounce

      return () => clearTimeout(timer)
    }
  }, [content, lastSavedContent, onSave])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        onSave()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onSave])

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (over && active.id !== over.id) {
        const oldIndex = content.sections.findIndex((_, i) => `section-${i}` === active.id)
        const newIndex = content.sections.findIndex((_, i) => `section-${i}` === over.id)
        onChange({
          ...content,
          sections: arrayMove(content.sections, oldIndex, newIndex),
        })
      }
    },
    [content, onChange]
  )

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const addSection = (type: string) => {
    const newSection = {
      type,
      title: SECTION_TYPES.find((s) => s.type === type)?.label || 'New Section',
      content: getDefaultSectionContent(type),
    }
    onChange({ ...content, sections: [...content.sections, newSection] })
    setExpandedSections((prev) => new Set(prev).add(content.sections.length))
  }

  const removeSection = (index: number) => {
    onChange({ ...content, sections: content.sections.filter((_, i) => i !== index) })
    setExpandedSections((prev) => {
      const next = new Set<number>()
      prev.forEach((i) => {
        if (i < index) next.add(i)
        else if (i > index) next.add(i - 1)
      })
      return next
    })
  }

  const updateSectionTitle = (index: number, title: string) => {
    const newSections = [...content.sections]
    newSections[index] = { ...newSections[index], title }
    onChange({ ...content, sections: newSections })
  }

  const updateSectionContent = (index: number, sectionContent: unknown) => {
    const newSections = [...content.sections]
    newSections[index] = { ...newSections[index], content: sectionContent }
    onChange({ ...content, sections: newSections })
  }

  const handleRegenerateSection = async (index: number) => {
    setRegeneratingSection(index)
    await onRegenerate('section', index)
    setRegeneratingSection(null)
  }

  const sectionIds = useMemo(
    () => content.sections.map((_, i) => `section-${i}`),
    [content.sections.length]
  )

  return (
    <div className={cn('grid gap-6', showPreview ? 'lg:grid-cols-2' : 'lg:grid-cols-1')}>
      {/* Editor Panel */}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="font-semibold">Content Editor</h2>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {autoSaveStatus === 'saving' && (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Saving...</span>
                </>
              )}
              {autoSaveStatus === 'saved' && (
                <>
                  <Check className="h-3 w-3 text-success" />
                  <span>Saved</span>
                </>
              )}
              {autoSaveStatus === 'unsaved' && (
                <>
                  <Cloud className="h-3 w-3" />
                  <span>Unsaved changes</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="btn-ghost btn-sm"
            >
              {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <button
              type="button"
              onClick={() => onRegenerate('content')}
              disabled={isRegenerating}
              className="btn-outline btn-sm"
            >
              {isRegenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              AI Regenerate
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              className="btn-primary btn-sm"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save
            </button>
          </div>
        </div>

        {/* Main Content Fields */}
        <div className="card space-y-4">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Page Header
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label mb-1 block">Title</label>
              <input
                value={content.title}
                onChange={(e) => onChange({ ...content, title: e.target.value })}
                className="input"
                placeholder="Site title"
              />
            </div>
            <div>
              <label className="label mb-1 block">Tagline</label>
              <input
                value={content.tagline}
                onChange={(e) => onChange({ ...content, tagline: e.target.value })}
                className="input"
                placeholder="Your tagline"
              />
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="card space-y-4">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Hero Section
          </h3>
          {siteId && (
            <div>
              <label className="label mb-1 block">Hero Image</label>
              <ImageUploader
                value={content.hero.image}
                onChange={(image) =>
                  onChange({ ...content, hero: { ...content.hero, image } })
                }
                siteId={siteId}
                aspectRatio="video"
                placeholder="Upload a hero image"
              />
            </div>
          )}
          <div>
            <label className="label mb-1 block">Headline</label>
            <input
              value={content.hero.headline}
              onChange={(e) =>
                onChange({ ...content, hero: { ...content.hero, headline: e.target.value } })
              }
              className="input"
              placeholder="Main headline"
            />
          </div>
          <div>
            <label className="label mb-1 block">Subheadline</label>
            <RichTextEditor
              content={content.hero.subheadline}
              onChange={(subheadline) =>
                onChange({ ...content, hero: { ...content.hero, subheadline } })
              }
              placeholder="Supporting text..."
              minHeight="80px"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label mb-1 block">CTA Button Text</label>
              <input
                value={content.hero.ctaText}
                onChange={(e) =>
                  onChange({ ...content, hero: { ...content.hero, ctaText: e.target.value } })
                }
                className="input"
                placeholder="Get Started"
              />
            </div>
            <div>
              <label className="label mb-1 block">CTA Button URL</label>
              <input
                value={content.hero.ctaUrl}
                onChange={(e) =>
                  onChange({ ...content, hero: { ...content.hero, ctaUrl: e.target.value } })
                }
                className="input"
                placeholder="#signup"
              />
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
              Page Sections
            </h3>
            <div className="relative group">
              <button type="button" className="btn-outline btn-sm">
                <Plus className="h-4 w-4" />
                Add Section
              </button>
              <div className="absolute right-0 top-full mt-2 w-48 bg-background border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
                <div className="py-1 max-h-64 overflow-auto">
                  {SECTION_TYPES.map((st) => (
                    <button
                      key={st.type}
                      type="button"
                      onClick={() => addSection(st.type)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                    >
                      {st.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {content.sections.map((section, index) => {
                  const SectionEditor = SECTION_EDITORS[section.type]
                  return (
                    <SortableSection
                      key={`section-${index}`}
                      id={`section-${index}`}
                      index={index}
                      title={section.title}
                      typeLabel={
                        SECTION_TYPES.find((s) => s.type === section.type)?.label || section.type
                      }
                      isExpanded={expandedSections.has(index)}
                      onToggleExpand={() => toggleSection(index)}
                      onRemove={() => removeSection(index)}
                      onTitleChange={(title) => updateSectionTitle(index, title)}
                      onRegenerateWithAI={() => handleRegenerateSection(index)}
                      isRegenerating={regeneratingSection === index}
                    >
                      {SectionEditor ? (
                        <SectionEditor
                          content={section.content as any}
                          onChange={(newContent) => updateSectionContent(index, newContent)}
                        />
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          No editor available for this section type.
                        </div>
                      )}
                    </SortableSection>
                  )
                })}
              </div>
            </SortableContext>
          </DndContext>

          {content.sections.length === 0 && (
            <div className="card text-center py-8 border-dashed">
              <p className="text-muted-foreground mb-2">No sections yet</p>
              <p className="text-sm text-muted-foreground">
                Click "Add Section" above to start building your page
              </p>
            </div>
          )}
        </div>

        {/* SEO Section */}
        <div className="card space-y-4">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            SEO Settings
          </h3>
          <div>
            <label className="label mb-1 block">Meta Title</label>
            <input
              value={content.seo.metaTitle}
              onChange={(e) =>
                onChange({ ...content, seo: { ...content.seo, metaTitle: e.target.value } })
              }
              className="input"
              placeholder="SEO title"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {content.seo.metaTitle.length}/60 characters
            </p>
          </div>
          <div>
            <label className="label mb-1 block">Meta Description</label>
            <textarea
              value={content.seo.metaDescription}
              onChange={(e) =>
                onChange({ ...content, seo: { ...content.seo, metaDescription: e.target.value } })
              }
              className="input min-h-[80px]"
              placeholder="SEO description"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {content.seo.metaDescription.length}/160 characters
            </p>
          </div>
        </div>
      </div>

      {/* Preview Panel */}
      {showPreview && (
        <div className="card p-0 overflow-hidden sticky top-4 h-fit max-h-[calc(100vh-120px)]">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/50">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-destructive/50" />
              <div className="w-3 h-3 rounded-full bg-accent/50" />
              <div className="w-3 h-3 rounded-full bg-success/50" />
            </div>
            <div className="flex-1 text-center">
              <span className="px-3 py-1 rounded-md bg-background text-xs text-muted-foreground">
                Live Preview
              </span>
            </div>
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
          </div>

          {previewLoading ? (
            <div className="aspect-video flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : previewHtml ? (
            <iframe
              srcDoc={previewHtml}
              className="w-full h-[600px] border-0"
              title="Live Preview"
            />
          ) : (
            <div className="aspect-video flex items-center justify-center text-muted-foreground">
              Preview not available
            </div>
          )}
        </div>
      )}
    </div>
  )
}
